import { Component, Inject, Input } from '@angular/core';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { SolutionParticipationService } from '../../service/solution-participation.service';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { AccountHierarchyResponse, AccountHierarchyStatus, SPRebateWidgetData, WidgetType } from '../../types/solution-participation-types';
import { BrandingService } from 'src/app/core/services/branding.service';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';
import { WidgetBarGraphType } from '../widget-bar-graph/widget-bar-graph.types';

@Component({
    selector: 'app-location-overview-gauge-chart',
    templateUrl: './location-overview-gauge-chart.component.html',
    styleUrls: ['./location-overview-gauge-chart.component.scss'],
    standalone: false
})

// TODO: Keep current name for backward compatibility with legacy usage.
// After full Neptune migration and legacy removal, rename this component since it is now a line-chart wrapper and no longer a gauge chart.
export class LocationOverviewGaugeChartComponent {

  @Input() useRefreshedUi: boolean | undefined;

  // Filter variables
  filters: ContractModel | undefined;
  filtersSubscription: Subscription | undefined;

  private hasProcessedMultipleContractsAll = false;

  //Variables
  widgetType: string = "all";
  chartTitle: string = "Average Solution Purchased";
  tooltipContent: string = '';
  //TODO: Once Neptune design is enabled, rename this to tooltipContent
  // Neptune tooltip HTML (same data as tooltipContent, pre-styled with Tailwind classes so the template just renders it)
  neptuneTooltipContent: string = '';
  widgetChartData: number[] = [];
  widgetChartColors: string[] = [];
  // Rebate target marker position for the Neptune bar graph (same value already fetched for eligibility checks)
  rebateComplianceTarget: number = 0;

  // Maps the current widget type to the Neptune overview bar graph's resolver key
  private readonly overviewBarGraphTypeMap: Record<string, WidgetBarGraphType> = {
    rebate: WidgetBarGraphType.OverviewRebate,
    mpl: WidgetBarGraphType.OverviewMpl,
    spl: WidgetBarGraphType.OverviewSpl,
    all: WidgetBarGraphType.OverviewAll
  };

  get isRefreshedSolutionParticipationUIEnabled(): boolean {
    return this._featureFlags.enableRefreshedSolutionParticipationUI;
  }

  get useRefreshedSolutionParticipationUI(): boolean {
    return this.useRefreshedUi ?? this.isRefreshedSolutionParticipationUIEnabled;
  }

  get overviewBarGraphType(): WidgetBarGraphType {
    return this.overviewBarGraphTypeMap[this.widgetType] ?? WidgetBarGraphType.OverviewAll;
  }

  // Data is not currently available for Rebate/MPL/SPL widgets for the Institutional division in IMEA.
  // Gated by a feature flag so this can be re-enabled via Azure App Configuration with no code change
  // once data becomes available.
  get isIMEAInstitutionalDivision(): boolean {
    return !this._featureFlags.enableSpRebateMPLSPLWidgetsForIMEAInstitutional &&
      (this.filters?.regions?.includes('IMEA') ?? false) &&
      this.filters?.division === 'Institutional';
  }

  // Flags
  displayWidgetLoader: boolean = true;
  multipleContractsSelected: boolean = false;
  multipleRebatesExistForContract: boolean = false;
  noProductList: boolean = false;
  eligibleForRebate: boolean = true;
  noRebatesNorProductLists : boolean = false;
  noSalesData : boolean = false;
  noDataAvailable: boolean = false;
  isRebateChart: boolean = false;
  showMessageBlock: boolean = false;
  multipleHierarchiesSelectedMessage: string = BrandingService.multipleHierarchiesSelectedMessage;

  //Subscriptions
  widgetTypeSubscription: Subscription | undefined;

  private CHART_COLORS: Record<WidgetType, [string, string]> = {
    all: ['#39B54A', '#E0E0E0'],
    spl: ['#1AA5E0', '#E0E0E0'],
    mpl: ['#006BD3', '#E0E0E0'],
    rebate: ['#813b9e', '#E0E0E0']
  };
  isOnlyAccountNumberFilterSelected: boolean = false;
  AccountHierarchyResponse?: AccountHierarchyResponse;

  constructor(
    private _filterService: FiltersService,
    private _spService: SolutionParticipationService,
    private _mixpanelService: MixpanelService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags
  ) { }

  ngOnInit(): void {
    // subscribe to filters
    this.filtersSubscription = this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);

    if (this.filters !== undefined) {


      this.isOnlyAccountNumberFilterSelected = ((this.filters?.accts?.length ?? 0) >= 1  &&
                                               (this.filters?.topLevelContractGroup?.length ?? 0) === 0 &&
                                               (this.filters?.secondLevelContractFilter?.length ?? 0)) === 0;

      //If Only Account Number filter is/are selected, check the account numbers belongs same hierarchy or not.
      // If they belong to different hierarchy, show the message to user.
      if (this.isOnlyAccountNumberFilterSelected) {
        this.AccountHierarchyResponse = this._spService.getAccountHierarchyData();
      }
      if (this?.AccountHierarchyResponse?.status === AccountHierarchyStatus.LinkedToMultipleHierarchies)
        this.multipleContractsSelected = true;
      else
        this.multipleContractsSelected = ((this.filters?.topLevelContractGroup?.length ?? 0) > 1 || (this.filters?.secondLevelContractFilter?.length ?? 0) > 1);

      this.widgetTypeSubscription = combineLatest(
        [this._spService.selectedRadioButtonObservable$,
          this._spService.SPGroupTableFirstLoadCompleteObservable$,
          this._spService.rebateWidgetDataObservable$,
          this._spService.mplWidgetDataObservable$,
          this._spService.splWidgetDataObservable$,
          this._spService.tableDataLoadingObservable$
        ])
      .pipe(distinctUntilChanged()).subscribe(([selectedRadioButton, firstLoadComplete, , , , isTableDataLoading]) => {
        // Re-read the flag from service to ensure we have the latest value
        this.multipleRebatesExistForContract = this._spService.getMultipleRebatesExistForContract();

        this.widgetType = selectedRadioButton;

        if(this.widgetType === "rebate")  {
          this.chartTitle = "Rebate Location";
          this.isRebateChart = true;
        } else if(this.widgetType === "mpl" || this.widgetType === "spl") {
          this.chartTitle = `Average ${this.widgetType.toUpperCase()} Solution Purchased`;
          this.isRebateChart = false;
        }
        else if (this.widgetType === "all"){
          this.chartTitle = 'Average Solution Purchased';
          this.isRebateChart = false;
        } else { //widgetType is "noData"
          this.chartTitle = '';
        }

        // The table data (and the rebate/mpl/spl widget data it's fetched alongside) is still being
        // (re)loaded for the current widget type selection - show the loader instead of computing a
        // message off of data/selection state that hasn't settled yet.
        if (isTableDataLoading) {
          this.displayWidgetLoader = true;
          this.showMessageBlock = false;
          return;
        }

        if (this.multipleContractsSelected) {
          this.noProductList = false;
          this.eligibleForRebate = true;

          if (this.widgetType !== 'all') {
              this.displayWidgetLoader = false;
              this.showMessageBlock = true;
              return;
          }

          this.showMessageBlock = false;

          if (!firstLoadComplete) {
            this.displayWidgetLoader = true;
            return;
          }

          // Only run the initialization once after the initial data load completes.
          if (!this.hasProcessedMultipleContractsAll) {
            this.hasProcessedMultipleContractsAll = true;
            void this.setWidgetChartData();
          }
          return;
        }

        if(this.multipleRebatesExistForContract){
          this.displayWidgetLoader = false;
          this.noProductList = false;
          this.eligibleForRebate = true;

          if (this.widgetType === 'rebate') {
              this.showMessageBlock = true;
              return;
          }
        }

        if(firstLoadComplete){
          this.setWidgetChartData();
        }
      });
    }
  }

  async setWidgetChartData() {
    this.displayWidgetLoader = true;
    this.widgetChartColors = this.CHART_COLORS[this.widgetType as WidgetType];

    await this.loadWidgetData();
    this.displayWidgetLoader = false;
  }

  async loadWidgetData(): Promise<void> {
    try {
      this.noProductList = false;
      this.eligibleForRebate = true;
      this.noRebatesNorProductLists = false;
      this.noSalesData = false;
      this.noDataAvailable = false;
      this.showMessageBlock = false;

      const isRebateMplOrSplWidget = this.widgetType === 'rebate' || this.widgetType === 'mpl' || this.widgetType === 'spl';
      if (isRebateMplOrSplWidget && this.isIMEAInstitutionalDivision) {
        this.noDataAvailable = true;
        this.showMessageBlock = true;
        return;
      }

      if (this.widgetType === 'rebate') {
        const rebateWidgetData = this._spService.getRebateWidgetData().data;
        const totalLocs = rebateWidgetData?.totalLocations ?? 0;
        const compliantLocs = rebateWidgetData?.totalCompliantLocations ?? 0;
        this.widgetChartData = [compliantLocs, totalLocs];
        this.rebateComplianceTarget = rebateWidgetData?.complianceTarget ?? 0;

        if (rebateWidgetData?.complianceTarget === 0 || rebateWidgetData?.complianceTarget == null) {
          this.eligibleForRebate = false;
          this.showMessageBlock = true;
          return;
        }

        this.updateRebateTooltipContent(rebateWidgetData);
      } else if(this.widgetType === 'mpl' || this.widgetType === 'spl'){
        // For MPL and SPL
        const productWidgetData = this.widgetType==='mpl' ? this._spService.getMPLWidgetData().data : this._spService.getSPLWidgetData().data;
        const productsCount = productWidgetData?.productListCount ?? 0;
        // round off to 2 decimal places
        const actualData = Math.round((productWidgetData?.avgProductListPurchased ?? 0) * 100) / 100;

        if(productsCount == 0) {
          this.noProductList = true;
          this.showMessageBlock = true;
        }

        this.widgetChartData = [actualData, productsCount];
        this.tooltipContent = `Average of ${this.widgetType.toUpperCase()} solution groups purchased across all locations`;
        this.neptuneTooltipContent = this.buildNeptuneSimpleTooltipContent(this.tooltipContent);
      } else if (this.widgetType === 'all') { // no rebate, mpl, or spl agreements exist
        const solutionPerLocation = parseFloat(this._spService.solutionsPerLocation.toFixed(1));
        const totalSolutionGroups = this._spService.numSolutions;
        this.widgetChartData = [solutionPerLocation, totalSolutionGroups];
        this.tooltipContent = `Average of solution groups purchased across all locations`;
        this.neptuneTooltipContent = this.buildNeptuneSimpleTooltipContent(this.tooltipContent);

        if(totalSolutionGroups == 0) {
          this.noSalesData = true;
          this.showMessageBlock = true;
        }
      }
      else if (this.widgetType === 'noData') { //no sales data at all
        this.noSalesData = true;
        this.showMessageBlock = true;
      }
    } catch (error) {
      console.error('Error loading widget data:', error);
    }
  }

  private updateRebateTooltipContent(rebateWidgetData: SPRebateWidgetData): void {
    // Get the list of solution groups (array of strings)
    const solutionGroups = rebateWidgetData?.solutionGroups ?? [];

    // Convert products to Regular Case
    const solutionGroupsArray = solutionGroups.map((product: string) =>
      product.trim().toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase())
    );

    const complianceTarget = rebateWidgetData?.complianceTarget !== null ? rebateWidgetData?.complianceTarget : 'N/A';
    const solutionGroupsBulletList = solutionGroupsArray.map((group) => `<li>${group}</li>`).join('');

    this.tooltipContent = `
        <b>Solution Groups:</b>
        <ul>${solutionGroupsBulletList}</ul>
        <br>
        <b>Compliance Target:</b> ${complianceTarget}
    `;

    const neptuneBulletList = solutionGroupsArray.map((group) => `<li class="text-xs leading-4 text-neptune-default">${group}</li>`).join('');
    this.neptuneTooltipContent = `
        <p class=" text-sm font-medium text-neptune-default tracking-[0.1px]">Solution Groups:</p>
        <ul class="list-disc pl-4 mt-4">${neptuneBulletList}</ul>
        <p class="text-xs font-semibold text-neptune-default mt-4 mb-1">Compliance Target: ${complianceTarget}</p>
    `;
  }

  // Simple one-line tooltip (MPL/SPL/All) styled the same way as the rebate Neptune tooltip, so other widget
  // types reuse the exact same panel/typography without needing template-side styling.
  private buildNeptuneSimpleTooltipContent(text: string): string {
    return `<p class=" text-xs text-neptune-default">${text}</p>`;
  }

  trackTooltipMixpanelEvent():void {
    this._mixpanelService.track('Solution Participation Location Widget Tooltip Hovered', {
      WidgetType: this.widgetType,
      H5s: this.filters?.topLevelContractGroup,
      H7s: this.filters?.secondLevelContractFilter,
      acctsSelected: this.filters?.accts
    });
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.widgetTypeSubscription?.unsubscribe();
  }
}
